export type SelectionRoot = Document | ShadowRoot;

/**
 * Stamped on each per-file diff section (see pr-v2-pierre-diff.tsx) and read
 * here to attribute code selections to a file.
 */
export const DIFF_FILENAME_ATTRIBUTE = 'data-diff-filename';

// Pierre's diff renderer stamps these on every rendered line: `data-line`
// holds the old-file number on deletion lines and the new-file number
// otherwise (see @pierre/diffs processLine).
const LINE_NUMBER_ATTRIBUTE = 'data-line';
const LINE_TYPE_ATTRIBUTE = 'data-line-type';
const DELETION_LINE_TYPE = 'change-deletion';

export type SelectedCodeLocation = {
  filename: string | null;
  startLine: number | null;
  endLine: number | null;
  /** True when only deleted lines are selected, so line numbers refer to the old file version. */
  isOldCode: boolean;
};

export type SelectedSnippet = {
  text: string;
  /** Null when the selection is not diff code or no diff metadata exists around it. */
  location: SelectedCodeLocation | null;
};

function getRootSelection(root: SelectionRoot): Selection | null {
  if (root instanceof Document) {
    return window.getSelection();
  }

  return (root as ShadowRoot & { getSelection?: () => Selection | null }).getSelection?.() ?? null;
}

function isUsableSelection(selection: Selection | null): selection is Selection {
  return Boolean(
    selection &&
    selection.rangeCount > 0 &&
    !selection.isCollapsed &&
    selection.toString().trim().length > 0,
  );
}

/**
 * Returns the current non-empty text selection from the provided roots.
 *
 * Callers pass explicit roots so feature owners decide which shadow DOM surfaces
 * are in scope instead of this shared utility crawling the whole document.
 */
export function getActiveSelection(selectionRoots: readonly SelectionRoot[]): Selection | null {
  for (const root of selectionRoots) {
    const selection = getRootSelection(root);
    if (isUsableSelection(selection)) {
      return selection;
    }
  }

  return null;
}

/** Range endpoints can be text nodes; resolve the element that owns them. */
function getElementFromNode(node: Node | null): Element | null {
  if (!node) {
    return null;
  }

  return node instanceof Element ? node : node.parentElement;
}

/**
 * Finds the `data-diff-filename` owner for a selection. Pierre renders diff code
 * inside open shadow roots while the per-file wrapper lives in the light DOM, so
 * the lookup hops shadow boundaries that `closest` cannot cross.
 */
function findSelectionFilename(range: Range): string | null {
  let current: Node | null = range.commonAncestorContainer;
  while (current) {
    // A selection can span a whole shadow tree, making the root itself the
    // common ancestor; resume the walk from its host.
    if (current instanceof ShadowRoot) {
      current = current.host;
      continue;
    }

    const element = getElementFromNode(current);
    if (!element) {
      return null;
    }

    const owner = element.closest(`[${DIFF_FILENAME_ATTRIBUTE}]`);
    if (owner) {
      return owner.getAttribute(DIFF_FILENAME_ATTRIBUTE);
    }

    const root = element.getRootNode();
    current = root instanceof ShadowRoot ? root.host : null;
  }

  return null;
}

type LineRange = {
  startLine: number;
  endLine: number;
  isOldCode: boolean;
};

/**
 * Derives the selected line range from Pierre line elements (`data-line` holds the
 * old-file number on deletion lines and the new-file number otherwise). When both
 * diff sides are selected, addition/context lines win so the range refers to the
 * new file version.
 */
function resolveLineRange(lineElements: readonly Element[]): LineRange | null {
  const entries = lineElements
    .map((element) => ({
      line: Number.parseInt(element.getAttribute(LINE_NUMBER_ATTRIBUTE) ?? '', 10),
      isDeletion: element.getAttribute(LINE_TYPE_ATTRIBUTE) === DELETION_LINE_TYPE,
    }))
    .filter((entry) => Number.isFinite(entry.line));

  if (entries.length === 0) {
    return null;
  }

  const isOldCode = entries.every((entry) => entry.isDeletion);
  const preferred = isOldCode ? entries : entries.filter((entry) => !entry.isDeletion);
  const lines = preferred.map((entry) => entry.line);

  return {
    startLine: Math.min(...lines),
    endLine: Math.max(...lines),
    isOldCode,
  };
}

/**
 * Selections that stay inside a single line clone without their line wrappers,
 * so the line element must come from the live range endpoints instead.
 */
function getEndpointLineElements(range: Range): Element[] {
  return [range.startContainer, range.endContainer]
    .map((node) => getElementFromNode(node)?.closest(`[${LINE_NUMBER_ATTRIBUTE}]`) ?? null)
    .filter((element): element is Element => element !== null);
}

function getSelectionCodeLocation(
  range: Range,
  fragmentLineElements: readonly Element[],
  options: { isKnownCode: boolean },
): SelectedCodeLocation | null {
  const lineRange =
    resolveLineRange(fragmentLineElements) ?? resolveLineRange(getEndpointLineElements(range));

  // Without a line range, only selections we already know are code may claim a
  // file, and a file is then still required for the location to mean anything.
  if (!lineRange && !options.isKnownCode) {
    return null;
  }

  const filename = findSelectionFilename(range);
  if (!filename && !lineRange) {
    return null;
  }

  return {
    filename,
    startLine: lineRange?.startLine ?? null,
    endLine: lineRange?.endLine ?? null,
    isOldCode: lineRange?.isOldCode ?? false,
  };
}

/**
 * Formats a snippet for the chat input, prefixing a GitHub-style location header
 * (`path#L1-L2`) when known so the model can anchor on the real file instead of
 * a floating snippet.
 */
export function formatSelectedSnippetForChat(snippet: SelectedSnippet): string {
  const { location, text } = snippet;
  if (!location?.filename) {
    return text;
  }

  // Filenames come from PR diffs, so a crafted name could carry newlines;
  // collapse whitespace so it cannot fabricate extra lines around the header.
  const filename = location.filename.replace(/\s+/g, ' ').trim();
  if (!filename) {
    return text;
  }

  let header = filename;
  if (location.startLine != null) {
    header +=
      location.endLine != null && location.endLine !== location.startLine
        ? `#L${location.startLine}-L${location.endLine}`
        : `#L${location.startLine}`;
  }
  if (location.isOldCode) {
    header += ' (deleted code)';
  }

  return `${header}\n${text}`;
}

/**
 * Gets the currently selected text, cleaning it of line numbers and other artifacts,
 * along with the diff location (filename and line range) when the selection comes
 * from diff code.
 * Uses the Selection API and preserves the original selection.
 * Only returns text from the selected side of the diff.
 *
 * @returns SelectedSnippet | null The selected snippet, or null if no text is selected
 */
export async function getSelectedSnippet(
  selection: Selection | null,
): Promise<SelectedSnippet | null> {
  if (!selection || selection.rangeCount === 0) {
    return null;
  }

  // Ensure document has focus
  if (!document.hasFocus()) {
    window.focus();
    document.body.focus();
  }

  const range = selection.getRangeAt(0);
  const fragment = range.cloneContents();
  const tempDiv = document.createElement('div');
  tempDiv.appendChild(fragment);

  // Find all selected code cells.
  const codeElements = Array.from(
    tempDiv.querySelectorAll('[data-column-content], td.diff-code'),
  ) as HTMLElement[];
  if (codeElements.length === 0) {
    const codeContainer = tempDiv.querySelector('[data-code]');
    const text = codeContainer?.textContent?.trim() || tempDiv.textContent?.trim() || null;
    if (!text) {
      return null;
    }

    return {
      text,
      location: getSelectionCodeLocation(range, [], { isKnownCode: Boolean(codeContainer) }),
    };
  }

  // Group code elements by their parent line to maintain line structure
  const lineGroups = new Map<HTMLElement, HTMLElement[]>();
  codeElements.forEach((el) => {
    const line = el.closest('tr, [data-line-index], [data-line], [data-alt-line]');
    if (line instanceof HTMLElement) {
      const elements = lineGroups.get(line) || [];
      elements.push(el);
      lineGroups.set(line, elements);
    }
  });
  if (lineGroups.size === 0) {
    const text = tempDiv.textContent?.trim() || null;
    if (!text) {
      return null;
    }

    return { text, location: getSelectionCodeLocation(range, [], { isKnownCode: true }) };
  }

  // Process each line, taking only the selected side's content
  const lines: string[] = [];
  lineGroups.forEach((elements) => {
    // If we have both sides selected for this line, take the one that has more selected text
    if (elements.length >= 2) {
      const leftText = elements[0]?.textContent || '';
      const rightText = elements[1]?.textContent || '';
      lines.push(leftText.length > rightText.length ? leftText : rightText);
    } else if (elements.length === 1) {
      // If only one side is selected, use that
      const text = elements[0]?.textContent || '';
      lines.push(text);
    }
  });

  const text = lines.join('\n').trim();
  if (!text) {
    return null;
  }

  return {
    text,
    location: getSelectionCodeLocation(range, Array.from(lineGroups.keys()), {
      isKnownCode: true,
    }),
  };
}
